import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Combobox } from '@headlessui/react';
import { GetCompanySearchResponse } from '@iwoca/lapi-client/edge';
import { GetApiIntroducerPortalDirectorSearchResponse } from '@iwoca/lapi-client/introducer_portal';
import { Label, Message, Tooltip } from '@iwoca/orion';
import { SearchableDropdown } from 'components/SearchableDropdown/SearchableDropdown';
import { fetchGetCompanySearch } from 'lendingApi/lendingApi';
import styles from './CompanyInputUK.module.css';

type CompanySearchCompany = GetCompanySearchResponse['data'][number];
type TProps = {
  setSelectedCompany: Dispatch<
    SetStateAction<GetCompanySearchResponse['data'][number] | undefined>
  >;
  setSelectedPerson?: (
    person:
      | GetApiIntroducerPortalDirectorSearchResponse['data'][number]
      | undefined,
  ) => void;
  onChange: (company: CompanySearchCompany) => void;
  tooltip?: string;
  error?: string;
  inputClassName?: string;
  disabled?: boolean;
};

export const CompanyInputUK = ({
  onChange,
  setSelectedCompany,
  setSelectedPerson,
  error,
  disabled,
  inputClassName,
  tooltip = 'Companies house searches both current and previous company names. If you cannot find the business you are looking for try using the company number.',
}: TProps) => {
  // Each time the API is called, we add one to this value. This let's us continue to show
  // the spinner until the last call is resolved
  const [numberOfCallsLoading, setNumberOfCallsLoading] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [companyOptions, setCompanyOptions] = useState<CompanySearchCompany[]>(
    [],
  );
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const optionSearch = setTimeout(async () => {
      if (searchQuery) {
        setNumberOfCallsLoading((prevNumber) => prevNumber + 1);

        try {
          const response = await fetchGetCompanySearch({
            companyName: searchQuery,
          });
          setCompanyOptions(response.data || []);
        } catch {
          setHasError(true);
          setCompanyOptions([]);
          unset();
        }
      } else {
        unset();
        return setCompanyOptions([]);
      }

      setNumberOfCallsLoading((prevNumber) => prevNumber - 1);
    }, 200);

    return () => {
      clearTimeout(optionSearch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const unset = () => {
    setSelectedCompany(undefined);
    setSelectedPerson?.(undefined);
  };

  return (
    <>
      <SearchableDropdown
        onChange={onChange}
        onSearch={setSearchQuery}
        optionsLoading={numberOfCallsLoading > 0}
        inputClassName={inputClassName}
        options={companyOptions}
        optionRenderer={(company) => `${company.name} | ${company.number}`}
        valueRenderer={(company) => `${company.name} | ${company.number}`}
        placeholder="Search for a company name or number..."
        onClear={unset}
        disabled={disabled}
        labelComponent={
          <>
            <Combobox.Label>
              <Label>Company name or number</Label>
            </Combobox.Label>
            <Tooltip dataTestId="company-search-tooltip" content={tooltip} />
          </>
        }
      />

      {(hasError || error) && (
        <Message
          className={styles.error}
          message={
            error ||
            'Unable to retrieve company details. Please try again later.'
          }
          type="error"
        />
      )}
    </>
  );
};
